/**
 * components/IntentEditorDrawer.tsx
 * Intent Editor drawer (softknack). Verbatim from HTML (~717-799). 580px.
 * Two tabs (Configuration / Versions). Wired to sk-orchestrator voice-setup:
 * getIntentDetail loads real versions/utterances/prompt on open; Save cuts a
 * new version, enrichment chips add a binding, and version rows set current.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { View, Text, Pressable, ScrollView, TextInput } from 'react-native';
import { useTheme } from '../context/themeContext';
import Icon from '../components/icon';
import { Chip, Button, Drawer } from '../components/voiceUi';
import { CATALOG, CatalogIntent } from '../data/voiceMock';
import { humanize, intentTitle } from '../utils/format';
import { getIntentDetail, addEnrichment, cutVersion, setCurrentVersion, IntentVersion, listMcpTools, type McpTool } from '../services/voice/voiceService';
import { showToast } from '../components/toastComponent';
import { handleApiError } from '../utils/errorHandler';

// One version-lineage row rendered in the Versions tab (from live detail).
type VersionRow = IntentVersion;

// Format an ISO createdAt into the "Mon D, YYYY" label the design uses.
function fmtDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function IntentEditorDrawer({ open, onClose, slug, intentId, source, onSaved }:
  { open: boolean; onClose: () => void; slug: string; intentId?: string; source?: CatalogIntent[]; onSaved?: () => void }) {
  const { theme } = useTheme();
  const c = theme.colors;

  // Prefer the live catalog passed from the screen; fall back to the mock list.
  const list = source && source.length ? source : CATALOG;
  const intent = list.find(i => i.slug === slug) || list[0];

  const [tab, setTab] = useState<'config' | 'versions'>('config');
  const [category, setCategory] = useState(intent.category);
  const [desc, setDesc] = useState(intent.desc);
  const [prompt, setPrompt] = useState('');
  const [enrich, setEnrich] = useState<string[]>(intent.enrich);
  const [utter, setUtter] = useState<string[]>(intent.utter);
  const [utterInput, setUtterInput] = useState('');

  // Live version lineage from the detail read (empty until loaded); replaces the
  // old fabricated VER_DATES lineage. Current version's prompt seeds the editor.
  const [versions, setVersions] = useState<VersionRow[]>([]);
  const [busy, setBusy] = useState(false);

  // Real MCP enrichment tools (role ENRICHMENT or BOTH) for the chip picker.
  const [tools, setTools] = useState<McpTool[]>([]);
  useEffect(() => {
    let cancelled = false;
    void listMcpTools().then(({ data }) => { if (!cancelled) setTools(data); });
    return () => { cancelled = true; };
  }, []);
  const enrichOpts = useMemo(() => tools.filter(t => t.role === 'ENRICHMENT' || t.role === 'BOTH').map(t => t.name), [tools]);

  // Load the real detail (versions/utterances/current prompt) when the drawer
  // opens or the edited intent changes. Config fields seed from the list row,
  // then get overwritten by the authoritative detail where the detail owns it.
  const loadDetail = useCallback(async () => {
    setTab('config');
    setCategory(intent.category);
    setDesc(intent.desc);
    setPrompt('');
    setEnrich(intent.enrich);
    setUtter(intent.utter);
    setUtterInput('');
    setVersions([]);
    if (!intentId) return; // mock/demo row without a backend id — nothing to fetch
    try {
      const { data } = await getIntentDetail(intentId);
      if (!data) return;
      setCategory(data.category);
      setDesc(data.desc);
      setEnrich(data.enrich);
      setUtter(data.utter);
      setVersions(data.versions);
      setPrompt(data.currentPrompt); // seed the editor from the current version
    } catch (err) {
      handleApiError(err, 'Could not load intent detail');
    }
  }, [intentId, slug]);

  useEffect(() => { if (open) void loadDetail(); }, [open, loadDetail]);

  const labelStyle = { fontSize: 10.5, fontWeight: '600' as const, letterSpacing: 0.55, textTransform: 'uppercase' as const, color: c.ink4, fontFamily: theme.font.sans, marginBottom: 6 };
  const inputStyle = { height: 36, paddingHorizontal: 12, borderWidth: 1, borderColor: c.lineStrong, borderRadius: 7, backgroundColor: c.card, color: c.ink, fontSize: 13, fontFamily: theme.font.sans } as any;
  const textareaStyle = { minHeight: 64, paddingHorizontal: 12, paddingVertical: 9, borderWidth: 1, borderColor: c.lineStrong, borderRadius: 7, backgroundColor: c.card, color: c.ink, fontSize: 13, fontFamily: theme.font.sans, textAlignVertical: 'top' as const } as any;
  const readOnlyBox = { height: 36, flexDirection: 'row' as const, alignItems: 'center' as const, paddingHorizontal: 12, borderWidth: 1, borderColor: c.lineStrong, borderRadius: 7, backgroundColor: c.paper2 };

  // Enrichment binding is add-only on the backend (no unbind endpoint), so
  // turning a chip ON posts addEnrichment; turning OFF is local-only for now.
  const toggleEnrich = async (tool: string) => {
    const turningOn = !enrich.includes(tool);
    if (!turningOn || !intentId) {
      setEnrich(e => e.includes(tool) ? e.filter(x => x !== tool) : [...e, tool]);
      return;
    }
    if (busy) return;
    setBusy(true);
    try {
      await addEnrichment(intentId, tool);
      setEnrich(e => [...e, tool]);
      showToast('success', 'Enrichment added', `${tool} bound to ${humanize(slug)}.`);
    } catch (err) {
      handleApiError(err, 'Could not add enrichment');
    } finally {
      setBusy(false);
    }
  };
  const addUtter = () => {
    const v = utterInput.trim();
    if (!v) return;
    setUtter(u => [...u, v]);
    setUtterInput('');
  };
  const removeUtter = (idx: number) => setUtter(u => u.filter((_, i) => i !== idx));

  // Save cuts a new prompt version and makes it current, then refreshes the
  // catalog row and closes (matches the footer's "Save cuts a new version").
  const saveVersion = async () => {
    if (busy) return;
    if (!intentId) { showToast('error', 'Cannot save', 'This intent has no backend id yet.'); return; }
    if (!prompt.trim()) { showToast('error', 'Prompt required', 'Enter an intent prompt before saving.'); return; }
    setBusy(true);
    try {
      await cutVersion(intentId, prompt);
      showToast('success', 'New version saved', `${humanize(slug)} is now on a new current version.`);
      onSaved?.();
      onClose();
    } catch (err) {
      handleApiError(err, 'Could not save new version');
    } finally {
      setBusy(false);
    }
  };

  // Roll an older version back to current, then reload the lineage in place.
  const handleSetCurrent = async (versionNo: number) => {
    if (busy || !intentId) return;
    setBusy(true);
    try {
      await setCurrentVersion(intentId, versionNo);
      showToast('success', 'Version set current', `v${versionNo} is now the current version.`);
      onSaved?.();
      await loadDetail();
      setTab('versions');
    } catch (err) {
      handleApiError(err, 'Could not set current version');
    } finally {
      setBusy(false);
    }
  };

  // Prefer the live version count once loaded; fall back to the list row's ver.
  const verCount = versions.length || intent.ver;
  const currentVer = versions.find(v => v.isCurrent)?.versionNo ?? intent.ver;

  const TabPill = ({ id, label }: { id: 'config' | 'versions'; label: string }) => {
    const active = tab === id;
    return (
      <Pressable onPress={() => setTab(id)}
        style={{ flex: 1, height: 30, alignItems: 'center', justifyContent: 'center', borderRadius: 7, backgroundColor: active ? c.ink : 'transparent' }}>
        <Text style={{ fontSize: 12, fontWeight: '500', color: active ? c.paper : c.ink3, fontFamily: theme.font.sans }}>{label}</Text>
      </Pressable>
    );
  };

  return (
    <Drawer open={open} onClose={onClose} width={580}>
      {/* Header */}
      <View style={{ flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, paddingHorizontal: 18, paddingTop: 16, paddingBottom: 12, borderBottomWidth: 1, borderBottomColor: c.line }}>
        <View style={{ flex: 1 }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 9, marginBottom: 4 }}>
            <Text style={{ fontWeight: '600', fontSize: 16, letterSpacing: -0.16, color: c.ink, fontFamily: theme.font.sans }}>{intentTitle(intent)}</Text>
            <Chip kind="mono">{intent.slug}</Chip>
          </View>
          <Text style={{ fontSize: 11.5, color: c.ink3, fontFamily: theme.font.sans }}>{intent.category} · live in {intent.tenants} tenants · current v{currentVer} · {verCount} versions</Text>
        </View>
        <Pressable onPress={onClose} style={{ width: 30, height: 30, alignItems: 'center', justifyContent: 'center', borderWidth: 1, borderColor: c.line, borderRadius: 7, backgroundColor: c.card }}>
          <Icon name="x" size={15} color={c.ink} />
        </Pressable>
      </View>

      {/* Tab pills */}
      <View style={{ paddingHorizontal: 18, paddingTop: 10 }}>
        <View style={{ flexDirection: 'row', gap: 4, padding: 4, backgroundColor: c.paper3, borderRadius: 9 }}>
          <TabPill id="config" label="Configuration" />
          <TabPill id="versions" label={`Versions · ${verCount}`} />
        </View>
      </View>

      {/* Body */}
      {tab === 'config' ? (
        <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: 18, gap: 18 }}>
          <View style={{ flexDirection: 'row', gap: 14 }}>
            <View style={{ flex: 1 }}>
              <Text style={labelStyle}>Owner</Text>
              <View style={readOnlyBox}><Text style={{ fontSize: 12.5, color: c.ink, fontFamily: theme.font.sans }}>{intent.ownerLabel}</Text></View>
            </View>
            <View style={{ flex: 1 }}>
              <Text style={labelStyle}>Spine tool</Text>
              <View style={readOnlyBox}><Text style={{ fontFamily: theme.font.mono, fontSize: 12, color: c.ink }}>{intent.spine}</Text></View>
            </View>
          </View>
          <Text style={{ fontSize: 11, color: c.ink4, marginTop: -8, lineHeight: 16.5, fontFamily: theme.font.sans }}>Owner and spine are structural — locked after creation.</Text>

          <View>
            <Text style={labelStyle}>Category</Text>
            <TextInput value={category} onChangeText={setCategory} style={inputStyle} />
          </View>
          <View>
            <Text style={labelStyle}>Description</Text>
            <TextInput value={desc} onChangeText={setDesc} multiline style={textareaStyle} />
          </View>
          <View>
            <Text style={labelStyle}>Intent prompt · versioned</Text>
            <TextInput value={prompt} onChangeText={setPrompt} multiline style={[textareaStyle, { minHeight: 96, fontFamily: theme.font.mono, fontSize: 12 }]} />
          </View>

          {/* Enrichment tools */}
          <View>
            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <Text style={[labelStyle, { marginBottom: 0 }]}>Enrichment tools · {enrich.length} bound</Text>
              <Text style={{ fontSize: 10.5, color: c.ink4, fontFamily: theme.font.sans }}>from MCP registry · many allowed</Text>
            </View>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 7 }}>
              {[...new Set([...enrich, ...enrichOpts])].map(tool => {
                const on = enrich.includes(tool);
                return (
                  <Pressable key={tool} onPress={() => toggleEnrich(tool)}
                    style={{ height: 28, paddingHorizontal: 11, borderRadius: 20, borderWidth: 1, alignItems: 'center', justifyContent: 'center', borderColor: on ? c.ink : c.line, backgroundColor: on ? c.ink : c.card }}>
                    <Text style={{ fontFamily: theme.font.mono, fontSize: 11.5, color: on ? c.paper : c.ink3 }}>{tool}</Text>
                  </Pressable>
                );
              })}
            </View>
          </View>

          {/* Slots (read-only) */}
          <View>
            <Text style={[labelStyle, { marginBottom: 8 }]}>Slots (from spine schema)</Text>
            <View style={{ gap: 6 }}>
              {intent.slots.map(sl => (
                <View key={sl} style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 12, paddingVertical: 9, borderWidth: 1, borderColor: c.line, borderRadius: 8, backgroundColor: c.paper2 }}>
                  <Text style={{ fontFamily: theme.font.mono, fontSize: 12, color: c.ink }}>{sl}</Text>
                  <Text style={{ fontSize: 10.5, color: c.ink4, fontFamily: theme.font.sans }}>read-only · from schema</Text>
                </View>
              ))}
            </View>
          </View>

          {/* Sample utterances */}
          <View>
            <Text style={[labelStyle, { marginBottom: 8 }]}>Sample utterances</Text>
            <View style={{ flexDirection: 'row', gap: 8, marginBottom: 9 }}>
              <TextInput value={utterInput} onChangeText={setUtterInput} onSubmitEditing={addUtter}
                placeholder="Type a caller phrase and press Enter" placeholderTextColor={c.ink4}
                style={[inputStyle, { flex: 1 }]} />
              <Button kind="default" label="Add" onPress={addUtter} />
            </View>
            {utter.length > 0 && (
              <View style={{ gap: 6 }}>
                {utter.map((u, idx) => (
                  <View key={`${u}-${idx}`} style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 8, paddingHorizontal: 12, paddingVertical: 8, borderWidth: 1, borderColor: c.line, borderRadius: 8 }}>
                    <Text style={{ fontSize: 12.5, color: c.ink2, fontFamily: theme.font.sans }}>“{u}”</Text>
                    <Pressable onPress={() => removeUtter(idx)}><Icon name="x" size={14} color={c.ink4} /></Pressable>
                  </View>
                ))}
              </View>
            )}
          </View>
        </ScrollView>
      ) : (
        <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: 18, gap: 10 }}>
          <Text style={{ fontSize: 11.5, color: c.ink3, lineHeight: 17, marginBottom: 2, fontFamily: theme.font.sans }}>Each version pins a slot template + intent prompt. One is current — that's what new calls use; completed calls stay pinned to the version they ran on.</Text>
          {[...versions].sort((a, b) => b.versionNo - a.versionNo).map(v => (
            <View key={v.versionNo} style={{ flexDirection: 'row', gap: 12, alignItems: 'center', paddingHorizontal: 12, paddingVertical: 11, borderWidth: 1, borderColor: v.isCurrent ? c.lineStrong : c.line, borderRadius: 9, backgroundColor: v.isCurrent ? c.paper2 : c.card }}>
              <Text style={{ width: 34, fontFamily: theme.font.mono, fontSize: 14, fontWeight: '600', color: c.ink }}>v{v.versionNo}</Text>
              <View style={{ flex: 1, minWidth: 0 }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                  <Text style={{ fontSize: 11, color: c.ink3, fontFamily: theme.font.sans }}>{fmtDate(v.createdAt)}</Text>
                  {v.isCurrent && <Text style={{ fontSize: 10, fontWeight: '600', color: c.ink, fontFamily: theme.font.sans }}>● current</Text>}
                </View>
                <Text style={{ fontSize: 12, color: c.ink2, lineHeight: 16.8, fontFamily: theme.font.sans }}>{v.isCurrent ? 'Current configuration' : 'Earlier version'}</Text>
                <Text style={{ fontSize: 10.5, color: c.ink4, fontFamily: theme.font.mono, marginTop: 3 }}>by {v.createdBy}</Text>
              </View>
              {!v.isCurrent && <Button kind="default" label="Set current" onPress={() => void handleSetCurrent(v.versionNo)} style={{ height: 28 }} />}
            </View>
          ))}
        </ScrollView>
      )}

      {/* Footer */}
      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 8, paddingHorizontal: 18, paddingVertical: 12, borderTopWidth: 1, borderTopColor: c.line, backgroundColor: c.paper2 }}>
        <Text style={{ fontSize: 11, color: c.ink4, fontFamily: theme.font.sans }}>Save cuts a new version and makes it current.</Text>
        <View style={{ flexDirection: 'row', gap: 8 }}>
          <Button kind="default" label="Cancel" onPress={onClose} />
          <Button kind="primary" label="Save new version" onPress={() => void saveVersion()} />
        </View>
      </View>
    </Drawer>
  );
}
